import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import { downloadFileFromUrl } from "@/utils/downloadUtils"
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"
import type { AxiosRequestConfig } from "axios"
import { apiClientV2 } from "../../../services/api/apiClient"
import { isAnalysisFailed } from "../utils/importStatus"
import type {
  AnalyzedFile,
  AnalyzeStarted,
  AnalyzeStatus,
  ColumnRole,
  CommitAllResult,
  CommitScheduled,
  CommitStatus,
  ErpCatalogEntry,
  ErpCompanySelection,
  ErpConnection,
  ErpConnectRequest,
  ErpOperationRequest,
  ErpPreview,
  ErpSystemId,
  ImportFile,
  ImportHistoryItem,
  MappingOverridePayload,
  StagedRowsPage,
  TargetTable,
} from "./types/imports"

// Re-export types so consumers can import everything from one place.
export type {
  AnalyzedFile,
  AnalyzeFileStatus,
  AnalyzeStatus,
  ColumnEntry,
  ColumnMapping,
  ColumnOverride,
  ColumnRole,
  CommitAllResult,
  CommitResult,
  CommitRowError,
  CommitScheduled,
  CommitStatus,
  CommitWarning,
  ConversionStatus,
  ErpAuthMethod,
  ErpCatalogEntry,
  ErpCompany,
  ErpCompanySelection,
  ErpConnection,
  ErpOperationRequest,
  ErpPreview,
  ErpSystemId,
  FileSummary,
  ImportFile,
  ImportHistoryItem,
  ImportStatus,
  MappingOverridePayload,
  MergeStrategy,
  SkippedComplianceColumn,
  SkippedSheet,
  StagedRow,
  StagedRowsPage,
  StagedSheetHeaders,
  SupplierNumberTwin,
  SupplierNumberTwinsMode,
  TargetTable,
} from "./types/imports"

// ─── Domain constants ─────────────────────────────────────────────────────────

const BASE = "/material-compliance/imports"

/**
 * Centralised query-key factory for the imports domain.
 * Shape follows the project convention: `["mc", "imports", ...specifics]`
 */
export const importQueryKeys = {
  all: ["mc", "imports"] as const,
  history: () => [...importQueryKeys.all, "history"] as const,
  /** Prefix key — use for broad invalidation across all fileId variants. */
  analyzedFilesAll: () => [...importQueryKeys.all, "analyzed-files"] as const,
  analyzedFiles: (fileIds: string[]) =>
    [...importQueryKeys.all, "analyzed-files", fileIds] as const,
  /** Prefix key — matches all analyze-status entries regardless of fileIds. */
  analyzeStatusAll: () => [...importQueryKeys.all, "analyze-status"] as const,
  analyzeStatus: (fileIds: string[] = []) =>
    [...importQueryKeys.all, "analyze-status", fileIds] as const,
  fileRows: (fileId: string, limit: number, sheetName?: string) =>
    [...importQueryKeys.all, "file-rows", fileId, limit, sheetName] as const,
  erpConnections: () => [...importQueryKeys.all, "erp-connections"] as const,
  erpCatalog: () => [...importQueryKeys.all, "erp-catalog"] as const,
} as const

// ─── API call functions ───────────────────────────────────────────────────────

/** Wire shape of `POST /imports/files/{id}/confirm`, snake_case fields under
 *  header-keyed (or, T1, sheet-scoped) overrides (see confirmMapping for why
 *  this is done by hand). */
export function toConfirmWireBody(payload: MappingOverridePayload) {
  const overrides: Record<
    string,
    {
      target_table?: TargetTable | null
      target_field?: string | null
      role?: ColumnRole | null
      source_sheet?: string | null
      source_column?: string | null
    }
  > = {}
  for (const [key, ov] of Object.entries(payload.overrides)) {
    const wire: (typeof overrides)[string] = {}
    if ("targetTable" in ov) wire.target_table = ov.targetTable ?? null
    if ("targetField" in ov) wire.target_field = ov.targetField ?? null
    if ("role" in ov) wire.role = ov.role ?? null
    // T1: scoped keys carry their sheet explicitly. The backend never splits
    // the key itself (sheet names may contain spaces).
    if ("sourceSheet" in ov) wire.source_sheet = ov.sourceSheet ?? null
    if ("sourceColumn" in ov) wire.source_column = ov.sourceColumn ?? null
    overrides[key] = wire
  }
  return {
    overrides,
    ...(payload.sheetPurposeOverrides !== undefined
      ? { sheet_purpose_overrides: payload.sheetPurposeOverrides }
      : {}),
    ...(payload.soldAsIs !== undefined ? { sold_as_is: payload.soldAsIs } : {}),
    ...(payload.productGroupReimportMode !== undefined
      ? { product_group_reimport_mode: payload.productGroupReimportMode }
      : {}),
    ...(payload.supplierNumberTwinsMode !== undefined
      ? { supplier_number_twins_mode: payload.supplierNumberTwinsMode }
      : {}),
  }
}

export const importsApi = {
  /** `GET /imports` — newest-first history, max 20 rows. */
  getHistory: (): Promise<ImportHistoryItem[]> =>
    apiClientV2.get(BASE).then((r) => r.data),

  /** `POST /imports/files` — upload a single file (multipart). */
  uploadFile: (file: File): Promise<ImportFile> => {
    const form = new FormData()
    form.append("file", file)
    return apiClientV2
      .post(`${BASE}/files`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data)
  },

  /** `POST /imports/files/{fileId}/analyze` — re-run analysis for one file whose analysis failed. */
  retryAnalysis: (fileId: string): Promise<AnalyzeStarted> =>
    apiClientV2.post(`${BASE}/files/${fileId}/analyze`).then((r) => r.data),

  /** `GET /imports/files/analyze/status` — per-file progress. Poll until `overall.inFlight === 0`. */
  getAnalyzeStatus: (fileIds: string[] = []): Promise<AnalyzeStatus> => {
    return apiClientV2
      .get(`${BASE}/files/analyze/status`, {
        params: { file_ids: fileIds },
        // FastAPI's `Query(list[str])` reads the repeated-key form
        // (`?file_ids=a&file_ids=b`), not axios v1's default bracket
        // notation — see conformityApi.ts's useConformityList for the same.
        paramsSerializer: { indexes: null },
      })
      .then((r) => r.data)
  },

  /** `GET /imports/files/analyzed` — full mapping JSON for the review screen. */
  getAnalyzedFiles: (fileIds: string[]): Promise<AnalyzedFile[]> => {
    return apiClientV2
      .get(`${BASE}/files/analyzed`, {
        params: { file_ids: fileIds },
        paramsSerializer: { indexes: null },
      })
      .then((r) => r.data)
  },

  /** `POST /imports/files/{fileId}/confirm` — apply column overrides and flip to `mapping-confirmed`.
   *  The body is serialized here, not by the interceptor: `overrides` is keyed
   *  by the spreadsheet header (or, T1, `"<sheet> <header>"`), and the generic
   *  camel-to-snake pass would turn "Warengruppe" into "_warengruppe" (and
   *  mangle the space in a scoped key), so the backend never matched a manual
   *  re-mapping of a capitalized header. */
  confirmMapping: (
    fileId: string,
    payload: MappingOverridePayload,
  ): Promise<AnalyzedFile> =>
    apiClientV2
      .post(`${BASE}/files/${fileId}/confirm`, toConfirmWireBody(payload), {
        skipCaseConversion: true,
      } as AxiosRequestConfig & { skipCaseConversion?: boolean })
      .then((r) => r.data),

  /**
   * `POST /imports/files/{fileId}/commit` — SCHEDULE the commit (runs async on
   * the worker; large files exceed the request timeout). Returns 202 with
   * status "committing"; poll `getCommitStatus` for the outcome.
   */
  commitFile: (fileId: string): Promise<CommitScheduled> =>
    apiClientV2.post(`${BASE}/files/${fileId}/commit`).then((r) => r.data),

  /** `GET /imports/files/{fileId}/commit/status` — poll target for the async commit. */
  getCommitStatus: (fileId: string): Promise<CommitStatus> =>
    apiClientV2
      .get(`${BASE}/files/${fileId}/commit/status`)
      .then((r) => r.data),

  /** `DELETE /imports/files/{fileId}` — remove a staged (non-committed) file. */
  deleteFile: (fileId: string): Promise<void> =>
    apiClientV2.delete(`${BASE}/files/${fileId}`).then(() => undefined),

  /** `GET /imports/files/{fileId}/rows` — paginated staged rows for the preview table. */
  getFileRows: (
    fileId: string,
    offset = 0,
    limit = 20,
    sheetName?: string,
  ): Promise<StagedRowsPage> => {
    return apiClientV2
      .get(`${BASE}/files/${fileId}/rows`, {
        params: { offset, limit, sheet_name: sheetName || undefined },
      })
      .then((r) => r.data)
  },

  /** `POST /imports/files/commit-all` — commit every mapping-ready file. */
  commitAll: (): Promise<CommitAllResult> =>
    apiClientV2.post(`${BASE}/files/commit-all`).then((r) => r.data),

  /** `GET /imports/erp/catalog` — supported ERPs + their auth method and objects. */
  getErpCatalog: (): Promise<ErpCatalogEntry[]> =>
    apiClientV2.get(`${BASE}/erp/catalog`).then((r) => r.data),

  /** `GET /imports/erp/connections` — ERPs this org has connected. */
  listErpConnections: (): Promise<ErpConnection[]> =>
    apiClientV2.get(`${BASE}/erp/connections`).then((r) => r.data),

  /** `POST /imports/erp/{erpType}/connect` — store a connection. Returns an
   * ErpCompanySelection instead when a multi-company MS tenant needs a choice. */
  connectErp: (
    erpType: ErpSystemId,
    body: ErpConnectRequest,
  ): Promise<ErpConnection | ErpCompanySelection> =>
    apiClientV2
      .post(`${BASE}/erp/${erpType}/connect`, body)
      .then((r) => r.data),

  /** `DELETE /imports/erp/{erpType}/connection` — disconnect. */
  disconnectErp: (erpType: ErpSystemId): Promise<void> =>
    apiClientV2
      .delete(`${BASE}/erp/${erpType}/connection`)
      .then(() => undefined),

  /** `POST /imports/erp/{erpType}/preview` — cheap sample + true total count. */
  previewErp: (
    erpType: ErpSystemId,
    body: ErpOperationRequest,
  ): Promise<ErpPreview> =>
    apiClientV2
      .post(`${BASE}/erp/${erpType}/preview`, body)
      .then((r) => r.data),

  /**
   * `POST /imports/erp/{erpType}/sync` — pull the full object from a connected
   * ERP. Stages it as a normal import (same `ImportFile` shape as an upload).
   */
  syncErp: (
    erpType: ErpSystemId,
    body: ErpOperationRequest,
  ): Promise<ImportFile> =>
    apiClientV2.post(`${BASE}/erp/${erpType}/sync`, body).then((r) => r.data),
}

// ─── Downloads ────────────────────────────────────────────────────────────────

/**
 * Download a staged file's source workbook (every sheet and row, not just the
 * preview's sample) under its own name. Asks for the file's analyzed entry
 * again instead of reusing the `downloadUrl` cached when the page loaded: the
 * link is presigned for 5 minutes and a mapping session easily outlasts it.
 *
 * Fetched and saved as a blob, never opened as a link: a browser ignores
 * `download` on a cross-origin S3 URL and navigates the tab instead, so a type
 * it can show (Safari renders text/csv) or an S3 error page would replace the
 * mapping screen and its unconfirmed overrides. A failed fetch rejects here.
 */
export async function downloadImportFile(fileId: string): Promise<void> {
  const files = await importsApi.getAnalyzedFiles([fileId])
  const file = files.find((f) => f.fileId === fileId)
  if (!file?.downloadUrl) {
    throw new Error(`No download link for import file ${fileId}`)
  }
  await downloadFileFromUrl(file.downloadUrl, file.filename)
}

// ─── Queries ──────────────────────────────────────────────────────────────────

/**
 * Import history list — committed + staged, newest first.
 *
 * Exported as options so a non-component caller (the agent-chat tool executor)
 * can read the same list through `fetchQuery` instead of a second GET.
 */
export const importHistoryQuery = {
  queryKey: importQueryKeys.history(),
  queryFn: importsApi.getHistory,
}

export function useImportHistory() {
  return useQuery(importHistoryQuery)
}

/** The lists that change when a staged file appears or is removed. */
export function invalidateImportFiles(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: importQueryKeys.analyzedFilesAll(),
  })
  queryClient.invalidateQueries({ queryKey: importQueryKeys.history() })
}

/**
 * Analyze status — polled every 2 s while `inFlight > 0`.
 * Analysis starts automatically server-side after upload, so polling also
 * continues through the brief `pending` window before parsing kicks in.
 * Pass `fileIds` to scope the response to a specific upload session.
 */
export function useAnalyzeStatus({
  enabled = false,
  fileIds = [],
}: { enabled?: boolean; fileIds?: string[] } = {}) {
  return useQuery({
    queryKey: importQueryKeys.analyzeStatus(fileIds),
    queryFn: () => importsApi.getAnalyzeStatus(fileIds),
    enabled,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return 2_000
      // Keep polling as long as any file hasn't reached a terminal state.
      // This covers the gap between xlsx conversion completing
      // (conversionStatus === "done") and AI extraction starting (inFlight > 0):
      // e.g. a file in "uploaded" or "extracting" status has inFlight = 0 but
      // is not yet ready to proceed to mapping.
      const TERMINAL_STATUSES: string[] = [
        "mapping-ready",
        "mapping-confirmed",
        "committed",
        "failed",
      ]
      // A failed analysis stays `uploaded` with its error, it will not move on its own.
      const stillProcessing = data.files.some(
        (f) => !TERMINAL_STATUSES.includes(f.status) && !isAnalysisFailed(f),
      )
      return data.overall.inFlight > 0 || stillProcessing ? 2_000 : false
    },
  })
}

/**
 * Analyzed files — full mapping JSON for the review screen.
 * `fileIds` is required and scopes the response to the current upload session.
 */
export function useAnalyzedFiles({
  enabled = true,
  fileIds,
}: {
  enabled?: boolean
  fileIds: string[]
}) {
  return useQuery({
    queryKey: importQueryKeys.analyzedFiles(fileIds),
    queryFn: () => importsApi.getAnalyzedFiles(fileIds),
    enabled: enabled && fileIds.length > 0,
  })
}

// ─── Mutations ────────────────────────────────────────────────────────────────

/** Upload a single file. Caller typically fires one mutation per file in parallel. */
export function useUploadImportFile() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (file: File) => importsApi.uploadFile(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: importQueryKeys.history() })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_IMPORTS_UPLOAD)
}

/**
 * Apply user column overrides for one file and flip it to `mapping-confirmed`.
 * Invalidates the analyzed-files cache so the review screen reflects the saved state.
 */
export function useConfirmMapping() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      fileId,
      payload,
    }: {
      fileId: string
      payload: MappingOverridePayload
    }) => importsApi.confirmMapping(fileId, payload),
    // Returned on purpose: TanStack Query awaits a promise from onSuccess
    // before mutateAsync resolves, so handleCommit's `await confirmMapping`
    // only continues once the analyzed list has been refetched. A
    // fire-and-forget refetch could resolve AFTER the commit flipped the row
    // to COMMITTING (which list_analyzed excludes) and empty the review step.
    // This only actually awaits a refetch while a `useAnalyzedFiles` observer
    // is enabled — TanStack Query skips a disabled query on invalidate — so
    // callers must confirm every file BEFORE disabling/freezing that query
    // (see the phase-ordering comment in MasterDataMappingModule's handleCommit).
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: importQueryKeys.analyzedFilesAll(),
      }),
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_IMPORTS_UPLOAD)
}

/**
 * Commit a file to master tables.
 * On success, invalidates only the history list.
 */
export function useCommitImportFile() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (fileId: string) => importsApi.commitFile(fileId),
    // Only the history moves here. The analyzed-files list must NOT be
    // refetched: the backend drops a COMMITTING row from that list, so a
    // refetch empties the review step (0 files, 0 records) while the commit
    // is still running. The review flow learns the outcome from
    // pollCommitStatus, never from this list.
    // The data-gaps counts (TUR-1539) are deliberately NOT invalidated here
    // either: this resolves when the commit is scheduled, and the nav pill
    // keeps that query active on every MC route, so a refetch now would cache
    // pre-commit counts as fresh. MasterDataMappingModule.handleCommit
    // invalidates once pollCommitStatus reports the commit landed.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: importQueryKeys.history() })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_IMPORTS_COMMIT)
}

/** Supported ERPs + capabilities — drives the connect cards and wizard. Rarely changes. */
export function useErpCatalog() {
  return useQuery({
    queryKey: importQueryKeys.erpCatalog(),
    queryFn: importsApi.getErpCatalog,
    staleTime: 60 * 60 * 1000,
  })
}

/** ERPs this org has connected — drives the Import-from-ERP tab. */
export function useErpConnections() {
  return useQuery({
    queryKey: importQueryKeys.erpConnections(),
    queryFn: importsApi.listErpConnections,
  })
}

/** Connect an ERP from a Microsoft sign-in token; refresh the connection list. */
export function useConnectErp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      erpType,
      body,
    }: {
      erpType: ErpSystemId
      body: ErpConnectRequest
    }) => importsApi.connectErp(erpType, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: importQueryKeys.erpConnections(),
      })
    },
  })
}

/** Disconnect an ERP; refresh the connection list. */
export function useDisconnectErp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (erpType: ErpSystemId) => importsApi.disconnectErp(erpType),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: importQueryKeys.erpConnections(),
      })
    },
  })
}

/**
 * Pull the full object from a connected ERP. Mirrors an upload: analysis starts
 * server-side, so the caller navigates to the processing screen with the
 * returned `fileId`. Refreshes the history list.
 */
export function useErpSync() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      erpType,
      body,
    }: {
      erpType: ErpSystemId
      body: ErpOperationRequest
    }) => importsApi.syncErp(erpType, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: importQueryKeys.history() })
    },
  })
}

/** Re-run analysis for one file whose analysis failed. */
export function useRetryAnalysis() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (fileId: string) => importsApi.retryAnalysis(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: importQueryKeys.history() })
      queryClient.invalidateQueries({
        queryKey: importQueryKeys.analyzeStatusAll(),
      })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_IMPORTS_UPLOAD)
}

/** Delete a staged file. Invalidates the analyzed-files cache. */
export function useDeleteImportFile() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (fileId: string) => importsApi.deleteFile(fileId),
    onSuccess: () => invalidateImportFiles(queryClient),
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_IMPORTS_DELETE)
}

/**
 * Paginated staged rows for the mapping-UI preview table.
 * queryKey scopes on fileId + sheetName, so switching either auto-resets
 * pagination instead of needing a manual reset effect.
 */
export function useFileRowsPages({
  fileId,
  limit = 10,
  sheetName,
}: {
  fileId: string
  limit?: number
  sheetName?: string
}) {
  return useInfiniteQuery({
    queryKey: importQueryKeys.fileRows(fileId, limit, sheetName),
    queryFn: ({ pageParam }) =>
      importsApi.getFileRows(fileId, pageParam, limit, sheetName),
    getNextPageParam: (last) =>
      last.hasMore ? last.offset + last.rows.length : undefined,
    initialPageParam: 0,
    enabled: !!fileId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Commit every mapping-ready file for the org in one call.
 * On success, refreshes both the analyzed-files cache and the history list.
 */
export function useCommitAllImportFiles() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: importsApi.commitAll,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: importQueryKeys.analyzedFilesAll(),
      })
      queryClient.invalidateQueries({ queryKey: importQueryKeys.history() })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_IMPORTS_COMMIT)
}
